import { useMemo } from 'react'
import moment from 'moment'
import { Button, Tag } from 'antd'
import * as Icons from '@ant-design/icons'
import { CheckGivenSupplierName } from 'admin/lib/SimpleDataValidation'

import {
  AutoCompleteFormItem,
  FormItem,
  FormRow,
  Select
} from 'admin/lib/Forms'
import { Delivery as fields } from 'admin/lib/Records'
import { Queries } from 'admin/lib/Api'
import { getCertStatus } from 'admin/lib/Certificates/CertificateExpiry'
import { Guid } from 'admin/lib/guid'
import * as ROUTES from 'admin/routes'
import { useFormContext } from 'react-hook-form'
import { generatePath } from 'react-router-dom'
import { RAW } from 'admin/pages/RecordEdit_v2/common/constants'

export const SupplierSelect = ({
  dataView = RAW,
  showRaw = false,
  isDisabled = (prop1, prop2, prop3) => false,
  isHidden = (prop1, prop2, prop3) => false
}) => {
  const { watch } = useFormContext()
  const selectedSupplierId = watch(fields.data.supplierId.path)

  const companies = Queries.useCompanies()
  const companyIsSelected =
    !!selectedSupplierId && selectedSupplierId !== Guid.Empty

  const companyEditPath = useMemo(
    () =>
      companyIsSelected
        ? generatePath(ROUTES.COMPANIES.EDIT, {
            companyId: selectedSupplierId
          })
        : '',
    [companyIsSelected, selectedSupplierId]
  )

  const selectedCompany = useMemo(() => {
    if (!companyIsSelected || !companies.isFetched) {
      return null
    }

    return companies.data?.find(
      company => company.companyId === selectedSupplierId
    )
  }, [
    companies.data,
    companies.isFetched,
    companyIsSelected,
    selectedSupplierId
  ])

  const knownSupplierCertifications = useMemo(() => {
    if (
      !selectedCompany?.companyCertificates ||
      selectedCompany.companyCertificates.length === 0
    ) {
      return ['(None)']
    }

    return selectedCompany.companyCertificates.map(cert => ({
      value:
        cert.certificationScheme +
        (cert.certificateNumber ? ` (${cert.certificateNumber})` : ''),
      status: getCertStatus(cert),
      expirationDate: moment(cert.expirationDate).local().format('DD/MM/YYYY')
    }))
  }, [selectedCompany])

  return (
    <>
      <AutoCompleteFormItem
        label={fields.data.supplierName.label}
        name={fields.data.supplierName.path}
        id="deliveryDataSupplierName"
        disabled={isDisabled(
          fields.data.supplierName.isRawField,
          dataView,
          showRaw
        )}
        hidden={isHidden(
          fields.data.supplierName.isRawField,
          dataView,
          showRaw
        )}
        simpleDataValidationRules={CheckGivenSupplierName}
      />

      <FormRow>
        <FormItem
          label={fields.data.supplierId.label}
          name={fields.data.supplierId.path}
          altNames={[fields.data.knownSupplierName.path]}
          disabled={isDisabled(
            fields.data.supplierId.isRawField,
            dataView,
            showRaw
          )}
          hidden={isHidden(
            fields.data.supplierId.isRawField,
            dataView,
            showRaw
          )}
        >
          <Select
            loading={!companies.isFetched}
            showSearch
            status={!companyIsSelected ? 'warning' : undefined}
          >
            <Select.Option aria-label="(None)" value={Guid.Empty}>
              (None)
            </Select.Option>

            {companies.data?.map(company => (
              <Select.Option
                key={company.companyId}
                value={company.companyId}
                aria-label={company.name}
              >
                {company.name}
              </Select.Option>
            ))}
          </Select>
        </FormItem>
        {!isHidden(fields.data.supplierId.isRawField, dataView, showRaw) && (
          <Button
            type="text"
            tabIndex="-1"
            icon={<Icons.EditFilled />}
            href={ROUTES.COMPANIES.LIST}
            target="_blank"
          />
        )}
      </FormRow>

      <AutoCompleteFormItem
        label={fields.data.givenSupplierCertifications.label}
        name={fields.data.givenSupplierCertifications.path}
        disabled={isDisabled(
          fields.data.givenSupplierCertifications.isRawField,
          dataView,
          showRaw
        )}
        hidden={isHidden(
          fields.data.givenSupplierCertifications.isRawField,
          dataView,
          showRaw
        )}
      />

      {companyIsSelected && (
        <FormRow>
          <FormItem
            label={
              fields.data.knownSupplierCertifications.label + ' (readonly)'
            }
            name={fields.data.knownSupplierCertifications.path}
            disabled
            hidden={isHidden(
              fields.data.knownSupplierCertifications.isRawField,
              dataView,
              showRaw
            )}
          >
            <div>
              {knownSupplierCertifications?.map(cert => (
                <Tag
                  key={cert.value}
                  color={cert.status?.color}
                  title={
                    cert.status?.text &&
                    `${cert.status?.text}: ${cert.expirationDate}`
                  }
                >
                  {cert.value || ''}
                </Tag>
              ))}
            </div>
          </FormItem>
          {!isHidden(
            fields.data.knownSupplierCertifications.isRawField,
            dataView,
            showRaw
          ) && (
            <Button
              type="text"
              tabIndex="-1"
              icon={<Icons.EditFilled />}
              href={companyEditPath}
              target="_blank"
              style={{ alignSelf: 'center' }}
            />
          )}
        </FormRow>
      )}
    </>
  )
}
